import { Link, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { QuoteHeader } from '@/lib/api-types'
import { formatDate, formatMoney } from '@/lib/format'
import { quoteChipClasses, quoteIsExpired } from '@/lib/status'
import { cn } from '@/lib/utils'
import { Card } from '@/components/ui/card'
import { DataTable, type DataTableColumn } from '@/components/ui/data-table'
import { EmptyState } from '@/components/ui/empty-state'
import { ErrorState } from '@/components/ui/error-state'
import { PageHeader } from '@/components/ui/page-header'
import { StatusChip } from '@/components/ui/status-chip'

function quoteStatusLabel(quote: QuoteHeader): string {
  return quote.status ?? '—'
}

const COLUMNS: DataTableColumn<QuoteHeader>[] = [
  {
    id: 'number',
    header: 'Quote #',
    widthClassName: 'w-28',
    cell: (q) => (
      <Link
        to={`/quotes/${q.id}`}
        onClick={(event) => event.stopPropagation()}
        className="rounded-sm font-mono text-[0.8125rem] text-inkMid no-underline underline-offset-4 hover:text-brand hover:underline"
      >
        {q.docNum ?? '—'}
      </Link>
    ),
  },
  {
    // No natural free-text field on a quote row — status takes the slack.
    id: 'status',
    header: 'Status',
    flex: true,
    widthClassName: 'min-w-36',
    cell: (q) => <StatusChip label={quoteStatusLabel(q)} chipClasses={quoteChipClasses(q.status)} />,
  },
  {
    id: 'issued',
    header: 'Issued',
    widthClassName: 'w-28',
    nowrap: true,
    cell: (q) => <span className="text-sm text-inkMid">{formatDate(q.issueDate)}</span>,
  },
  {
    id: 'expires',
    header: 'Expires',
    widthClassName: 'w-36',
    nowrap: true,
    cell: (q) => (
      <span className="text-sm text-inkMid">
        <ExpiryCell quote={q} />
      </span>
    ),
  },
  {
    id: 'total',
    header: 'Total',
    align: 'right',
    widthClassName: 'w-28',
    nowrap: true,
    cell: (q) => formatMoney(q.total),
  },
]

export function QuotesListPage() {
  const navigate = useNavigate()

  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ['quotes'],
    queryFn: ({ signal }) => api.listQuotes({ signal }),
    staleTime: 15_000,
  })

  return (
    <div className="mx-auto max-w-data">
      <PageHeader
        title="Quotes"
        description="Pricing your Green Line team has put in front of you."
      />

      <div className="mt-6">
        <DataTable<QuoteHeader>
          columns={COLUMNS}
          rows={data?.quotes ?? []}
          rowKey={(q) => q.id}
          onRowClick={(q) => navigate(`/quotes/${q.id}`)}
          caption="Your quotes from Green Line"
          loading={isPending}
          loadingLabel="Loading your quotes…"
          error={
            isError ? (
              <ErrorState message="We couldn’t load your quotes." onRetry={() => void refetch()} />
            ) : undefined
          }
          empty={
            <EmptyState title="Quotes from Green Line will appear here — ask your rep for pricing any time." />
          }
          renderCard={(q) => <QuoteCard quote={q} />}
        />
      </div>
    </div>
  )
}

function ExpiryCell({ quote }: { quote: QuoteHeader }) {
  const expired = quoteIsExpired(quote.expirationDate, quote.status)
  if (!quote.expirationDate) return <span className="text-mute">—</span>
  return (
    <span className={cn(expired ? 'font-medium text-rust' : undefined)}>
      {formatDate(quote.expirationDate)}
      {expired ? ' · expired' : ''}
    </span>
  )
}

/** Narrow-screen fallback: one tappable card per quote. */
function QuoteCard({ quote }: { quote: QuoteHeader }) {
  return (
    <Link to={`/quotes/${quote.id}`} className="block no-underline">
      <Card
        padding="sm"
        className="transition-colors duration-[180ms] ease-editorial hover:border-mute hover:bg-cream"
      >
        <span className="flex items-baseline justify-between gap-3">
          <span className="font-mono text-[0.8125rem] text-inkMid">
            Quote {quote.docNum ?? '—'}
          </span>
          <span className="font-mono text-[0.8125rem] text-inkMid">{formatMoney(quote.total)}</span>
        </span>
        <span className="mt-2.5 flex flex-wrap items-center gap-2 text-xs text-mute">
          <StatusChip label={quoteStatusLabel(quote)} chipClasses={quoteChipClasses(quote.status)} />
          <span>
            Expires <ExpiryCell quote={quote} />
          </span>
        </span>
      </Card>
    </Link>
  )
}
